import React, { useRef, useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';

/**
 * File picker button with camera/documents/gallery options on mobile.
 *
 * On desktop: clicking the button opens the standard file picker.
 * On mobile: clicking the button opens a menu with "Take photo" (camera),
 * "Choose document" (documents/PDFs), and "Choose from gallery" options.
 *
 * Props:
 *   onFiles        - callback receiving File[] (matches dropzone onDrop signature)
 *   accept         - dropzone-style accept object, e.g. { 'image/jpeg': ['.jpg'], 'application/pdf': ['.pdf'] }
 *   multiple       - allow multiple file selection (default: false)
 *   disabled       - disable the button
 *   children       - button content
 *   buttonProps    - additional props spread onto the MUI Button
 */

function flattenAccept(accept) {
  if (!accept) return undefined;
  return Object.keys(accept).join(',');
}

export default function FilePickerButton({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  children,
  buttonProps = {},
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(pointer: coarse)');
  const [menuAnchor, setMenuAnchor] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0 && onFiles) {
      onFiles(files);
    }
    // Reset so selecting the same file again triggers onChange
    event.target.value = '';
  };

  const handleClick = (event) => {
    if (disabled) return;
    if (isMobile) {
      setMenuAnchor(event.currentTarget);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
    setTimeout(() => setMenuAnchor(null), 100);
  };

  const handleDocumentsClick = () => {
    fileInputRef.current?.click();
    setTimeout(() => setMenuAnchor(null), 100);
  };

  const handleGalleryClick = () => {
    galleryInputRef.current?.click();
    setTimeout(() => setMenuAnchor(null), 100);
  };

  const acceptString = flattenAccept(accept);

  return (
    <>
      <Button disabled={disabled} onClick={handleClick} {...buttonProps}>
        {children}
      </Button>

      {/* Main file input (desktop + mobile documents) */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptString}
        multiple={multiple}
        hidden
        onChange={handleInputChange}
      />

      {/* Camera file input (mobile only) */}
      {isMobile && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={handleInputChange}
        />
      )}

      {/* Gallery file input (mobile only) */}
      {isMobile && (
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          hidden
          onChange={handleInputChange}
        />
      )}

      {/* Mobile menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleCameraClick}>
          <ListItemIcon>
            <CameraAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Take photo')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDocumentsClick}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Choose document')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleGalleryClick}>
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Choose from gallery')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
